/**
 * F-17-type: what KIND of script line each stretch of a cell's text is —
 * spoken narration, a sound cue, or a visual direction — stored as SWITCH
 * POINTS: "from here on, it is sound".
 *
 * PURE logic — no Yjs, no DOM. The doc side (the one flat `ty` cell key, C2)
 * lives in ./doc/schema.ts + ./doc/board.ts; the renderers consume the runs
 * through `segmentMarkedText` (text-marks.ts), which cuts the text by kinds
 * and by formatting marks at once.
 *
 * ## Why switch points, and not ranges like the marks
 *
 * A writer builds one of these cells in a single breath: type the visual,
 * hit `/`, pick Sound, type the cue, hit `/`, pick Narration, keep going. The
 * gesture is "the kind changes HERE", and what follows the caret does not
 * exist yet when the pick lands. A range `[start, end)` cannot describe that
 * — an empty range is nothing, and text typed at a range's end falls outside
 * it — while a switch point can: it sits at the caret, the text typed there
 * lands after it and so inside its run, and the run ends where the next switch
 * begins (or the text does). Narration before the first switch is the default,
 * so a cell with no switches is a narration line — which is what every cell
 * written before this feature holds.
 *
 * ## Anchoring
 *
 * The same model the marks use: the offset is a hint, the quoted text (the
 * first `TEXT_TYPE_QUOTE_MAX` characters of the run) is the anchor. The
 * writer's own edits are rebased exactly (`rebaseTextTypeSwitches`, inside the
 * value write's transaction); the quote search only ever covers a PEER's
 * concurrent edit. One difference from the marks, chosen on purpose: a switch
 * whose quote cannot be found stays at its stored offset instead of vanishing.
 * A mark that drops out loses some bold; a switch that dropped out would hand
 * a whole sound cue to the previous run — being a character or two off is the
 * lesser wrong.
 *
 * ## Invariants of the stored list
 *
 * Sorted by offset, one switch per offset, and none redundant (a switch to the
 * kind already in effect, or a leading switch to narration). Every writer goes
 * through `applyTextTypeSwitch` / `rebaseTextTypeSwitches`, which normalize;
 * readers normalize again defensively, since the doc is shared and a future
 * writer is not a proof.
 */
import { textSplice } from "./text-splice.js";
/** The kinds that need no registry entry: the ones the feature shipped with. */
export const BUILTIN_CELL_TEXT_TYPES = ["sound", "visual"];
/** @deprecated Use `BUILTIN_CELL_TEXT_TYPES`; kept for older importers. */
export const CELL_TEXT_TYPES = BUILTIN_CELL_TEXT_TYPES;
/** Longest stored kind id — a bound for the doc, not a format (ids are opaque). */
export const CELL_TEXT_TYPE_MAX_LENGTH = 64;
export function isBuiltinTextType(value) {
    return value === "sound" || value === "visual";
}
/**
 * Anything that can be a stored kind: a non-empty, bounded string. Deliberately
 * NOT a registry check — the registry is a REST resource the renderer may not
 * have loaded yet (or that an admin has since edited), and a switch dropped at
 * read time would hand its whole run to the previous kind (see the module
 * note on anchoring for why a switch must never vanish).
 */
export function isCellTextType(value) {
    return typeof value === "string" && value.length > 0 && value.length <= CELL_TEXT_TYPE_MAX_LENGTH;
}
/** How much of a run's text is stored as its re-anchoring quote. */
export const TEXT_TYPE_QUOTE_MAX = 64;
/* ------------------------------------------------------------------ */
/* Reading switches out of the doc                                     */
/* ------------------------------------------------------------------ */
/**
 * Coerce an untyped doc value into switches. Everything in the cells map is
 * `unknown` — written by this client, by a peer on an older build, or by a
 * server-side job — so a malformed entry renders as "no switch", never throws
 * inside a render pass.
 *
 * A bare `"sound"` / `"visual"` is the WHOLE-CELL form the feature first
 * shipped with (2026-09-02, one kind per line) and reads as a single switch
 * at offset 0 — so a board marked up that morning renders unchanged, and the
 * first pick on such a cell rewrites the key in the list form. Only the two
 * built-ins ever had that form, so only they are read from it.
 */
export function sanitizeTextTypeSwitches(raw) {
    if (isBuiltinTextType(raw))
        return [{ at: 0, quote: "", ty: raw }];
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const entry of raw) {
        if (typeof entry !== "object" || entry === null)
            continue;
        const { at, quote, ty } = entry;
        if (typeof at !== "number" || !Number.isInteger(at) || at < 0)
            continue;
        if (typeof quote !== "string")
            continue;
        if (ty !== undefined && !isCellTextType(ty))
            continue;
        const sw = { at, quote };
        if (ty !== undefined)
            sw.ty = ty;
        out.push(sw);
    }
    return out;
}
/**
 * Where `sw` sits in `text` now. The stored offset wins when the quote is
 * still there; otherwise the NEAREST occurrence of the quote to the stored
 * offset (a peer inserted or deleted text before the run); otherwise the
 * stored offset itself, clamped — never "gone" (see the module note).
 */
function placeSwitch(text, sw) {
    const clamped = Math.max(0, Math.min(sw.at, text.length));
    if (sw.quote.length === 0)
        return clamped;
    if (sw.at <= text.length && text.startsWith(sw.quote, sw.at))
        return sw.at;
    let best = null;
    for (let i = text.indexOf(sw.quote); i >= 0; i = text.indexOf(sw.quote, i + 1)) {
        if (best === null || Math.abs(i - sw.at) < Math.abs(best - sw.at))
            best = i;
    }
    return best ?? clamped;
}
/**
 * Sort (stably — a later entry at the same offset is the later switch and
 * wins), drop every switch to the kind already in effect, and so drop a
 * leading switch to narration. What is left describes the text and nothing
 * more; a cell whose text is all narration normalizes to an empty list.
 */
function normalizePlaced(placed) {
    const sorted = placed.map((sw, i) => ({ sw, i })).sort((a, b) => a.sw.at - b.sw.at || a.i - b.i);
    const out = [];
    let current;
    for (let i = 0; i < sorted.length; i++) {
        const sw = sorted[i].sw;
        const next = sorted[i + 1]?.sw;
        if (next !== undefined && next.at === sw.at)
            continue; // the later one at this offset wins
        if (sw.ty === current)
            continue; // redundant: already in effect
        out.push(sw.ty === undefined ? { at: sw.at } : { at: sw.at, ty: sw.ty });
        current = sw.ty;
    }
    return out;
}
/** Every switch resolved against `text`, normalized (see `normalizePlaced`). */
export function resolveTextTypeSwitches(text, switches) {
    return normalizePlaced(switches.map((sw) => ({ at: placeSwitch(text, sw), ty: sw.ty })));
}
/**
 * The sound and visual runs of `text`, in order, non-overlapping — what a
 * renderer brackets and inks. Narration is the gaps.
 *
 * Each run is shrunk to its non-whitespace content. The space a writer typed
 * before `/sound` belongs to the earlier run in the doc (the switch lands
 * where the `/` was), but what a reader sees is the bracket, and
 * `[a man enters ]` is wrong where `[a man enters] [door slams]` is right.
 * Only the drawing shrinks: the stored switch stays where the writer put it,
 * so typing there still continues in the kind they chose.
 */
export function textTypeRuns(text, switches) {
    const placed = resolveTextTypeSwitches(text, switches);
    const runs = [];
    for (let i = 0; i < placed.length; i++) {
        const sw = placed[i];
        if (sw.ty === undefined)
            continue;
        let start = sw.at;
        let end = placed[i + 1]?.at ?? text.length;
        while (start < end && isSpace(text.charAt(start)))
            start++;
        while (end > start && isSpace(text.charAt(end - 1)))
            end--;
        if (end > start)
            runs.push({ start, end, ty: sw.ty });
    }
    return runs;
}
function isSpace(ch) {
    return /\s/.test(ch);
}
/** The kind in effect at `offset` — narration (undefined) outside every run. */
export function textTypeAt(text, switches, offset) {
    let current;
    for (const sw of resolveTextTypeSwitches(text, switches)) {
        if (sw.at > offset)
            break;
        current = sw.ty;
    }
    return current;
}
/* ------------------------------------------------------------------ */
/* Writing switches                                                    */
/* ------------------------------------------------------------------ */
/**
 * Re-anchor placed (sorted) switches to `text` — the stored form of a write.
 * A quote stops at the NEXT switch: it anchors its own run, and a quote that
 * ran on into the following run would break the moment that run was edited.
 */
function toStored(text, placed) {
    return placed.map((sw, i) => {
        const at = Math.max(0, Math.min(sw.at, text.length));
        const end = Math.min(at + TEXT_TYPE_QUOTE_MAX, placed[i + 1]?.at ?? text.length);
        const stored = { at, quote: text.slice(at, Math.max(at, end)) };
        if (sw.ty !== undefined)
            stored.ty = sw.ty;
        return stored;
    });
}
/**
 * Switch to `ty` (narration when undefined) at `at` — the write behind
 * picking a kind from the `/` menu, `at` being where the `/` was. A switch
 * already at that offset is replaced: the writer retyping the command there
 * is changing their mind, not stacking a second answer.
 *
 * Returns switches re-anchored to `text`, normalized — which is also what
 * drops a pick that changes nothing, so "/narration" on a plain line leaves
 * the key absent exactly as it was.
 */
export function applyTextTypeSwitch(text, switches, at, ty) {
    const offset = Math.max(0, Math.min(at, text.length));
    const kept = resolveTextTypeSwitches(text, switches).filter((sw) => sw.at !== offset);
    const added = ty === undefined ? { at: offset } : { at: offset, ty };
    return toStored(text, normalizePlaced([...kept, added]));
}
/**
 * Shift `switches` from `before` to `after` — called on the way INTO the doc,
 * so a cell's switches describe the text the same transaction is writing.
 *
 * The edit is read as ONE replaced middle (`textSplice`), the reading the
 * marks and `spliceYText` share. A switch after the cut moves by the length
 * delta; one inside the replaced text collapses to the cut's start (two
 * collapsing onto the same offset leave the later one, per normalization);
 * and a switch AT the cut's start stays put — which is the rule that makes
 * this feature work: text typed at a switch lands after it, in its run, so
 * picking a kind and then typing is all a writer has to do.
 */
export function rebaseTextTypeSwitches(before, after, switches) {
    const placed = resolveTextTypeSwitches(before, switches);
    if (before === after)
        return toStored(after, placed);
    const { cutStart, cutEnd, delta } = textSplice(before, after);
    const moved = placed.map((sw) => {
        const at = sw.at <= cutStart ? sw.at : sw.at >= cutEnd ? sw.at + delta : cutStart;
        return sw.ty === undefined ? { at } : { at, ty: sw.ty };
    });
    return toStored(after, normalizePlaced(moved));
}
//# sourceMappingURL=text-types.js.map